import React from "react";
import Link from "next/link";
import { UrlObject } from "url";
import { Variants, motion } from "motion/react";
import { classes } from "lib/actions/util";
import { useRouter } from "next/router";
import { useSideNav } from "lib/contexts/ProjectContexts/SideNavContext";
import SideNavTooltip from "./SideNavTooltip";

export default function SideNavItem(p: {
  /**
   * Pass a render function to style the icon's own selected state (e.g. the
   * coloured tile). A plain node keeps the left-edge bar as the indicator.
   */
  icon: React.ReactNode | ((isActive: boolean) => React.ReactNode);
  label: string;
  disabled?: boolean;
  href?: string | UrlObject;
  isActive?: boolean;
  activeRegex?: RegExp;
  onClick?: () => void;
}) {
  const router = useRouter();
  const { isExpanded, collapseWhenUsed, toggleExpanded } = useSideNav();

  const isActive = p.isActive ?? p.activeRegex?.test(router.asPath);
  const isDisabled = p.disabled;
  const hasIconActiveState = typeof p.icon === "function";

  const variants: Variants = {
    expanded: {
      marginLeft: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
      opacity: 1,
    },
    collapsed: {
      marginLeft: -500,
      transition: {
        duration: 0.5,
        ease: "easeIn",
        delay: 0.1,
      },
      opacity: 0,
    },
  };

  function onClick() {
    if (!p.href) p.onClick?.();
    if (collapseWhenUsed) toggleExpanded(false);
  }

  let component = (
    <SideNavTooltip label={p.label}>
      <button
        type="button"
        className={classes(
          "relative flex h-14 w-full select-none items-center py-3.5 text-left",
          !p.disabled
            ? classes(
                isActive ? "text-icon-active" : "text-icon-normal",
                "hover:text-icon-hover",
              )
            : "text-icon-disabled",
        )}
        onClick={onClick}
        disabled={p.disabled}
        tabIndex={p.href ? -1 : undefined}
      >
        {isActive && !hasIconActiveState && (
          <div className="absolute inset-y-2 left-0">
            <div className="h-full border-r-2 border-gray-800" />
          </div>
        )}
        {typeof p.icon === "function" ? p.icon(!!isActive) : p.icon}
        <div
          className={classes(
            "overflow-hidden transition-opacity duration-300",
            isExpanded ? "opacity-100 delay-300" : "opacity-0",
          )}
        >
          <motion.span
            className="whitespace-nowrap"
            initial={false}
            variants={variants}
            animate={isExpanded ? "expanded" : "collapsed"}
          >
            {p.label}
          </motion.span>
        </div>
      </button>
    </SideNavTooltip>
  );
  //wrap in link
  if (p.href && !isDisabled)
    component = (
      <Link href={p.href} aria-label={p.label}>
        {component}
      </Link>
    );
  return component;
}
